import React, { useState, useRef, useEffect } from 'react';
import { Edit3, Printer, X, Save, RotateCcw } from 'lucide-react';
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from '../config';

const Section15Notice = () => {
    const { language, translations } = useLanguage();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const notificationContentRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [objectionData, setObjectionData] = useState([]);
    const [originalFormData, setOriginalFormData] = useState(null);

    const [loadingusersLAO, setLoadingusersLAO] = useState(false);
    const [usersLAO, setusersLAO] = useState([]);

    const [projectId, setProjectId] = useState(null);
    const [proposalId, setProposalId] = useState(null);
    const hasFetchedRef = useRef(false);

    const [editingObjectionId, setEditingObjectionId] = useState(null);
    const [objectionBackup, setObjectionBackup] = useState(null);

    const ROLES = {
        COLLECTOR: 1,
        LAO: 3,
        SAMANVAY: 6,
        PROJECT_INCHARGE: 18,
        DATA_ENTRY: 14
    };

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

    const laoDesi = usersLAO?.[0]?.designation || 'N/A';
    // Get project ID
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return {
                        id: projectData.project_id || projectData.id,
                        name: projectData.project_name || projectData.name
                    };
                } catch (e) {
                    console.error('Error parsing current project data:', e);
                }
            }

            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return {
                        id: project.project_id || project.id,
                        name: project.project_name || project.name
                    };
                } catch (e) {
                    console.error('Error parsing selected project:', e);
                }
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return {
                        id: user.data?.project_id || user.project_id,
                        name: user.data?.project_name || user.project_name || 'Unknown Project'
                    };
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            return { id: null, name: null };
        };

        const projectInfo = getProjectInfo();
        setProjectId(projectInfo.id);
    }, []);

    // Get proposal ID
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id ||
                            null
                    };
                } catch (e) {
                    console.error('Error parsing proposal data:', e);
                }
            }
            return { proposalId: null };
        };

        const proposalInfo = getProposalInfo();
        setProposalId(proposalInfo.proposalId);
    }, []);

    // Fetch objection data
    const fetchObjectionData = async () => {
        try {
            if (!projectId || !proposalId) return;

            const result = await apiService.getAllGeneralObjections(projectId, proposalId);
            console.log('Objection data fetched:', result);

            if (result.status === 200 && Array.isArray(result.data)) {
                // Add hearing_time if not present (default to formData.time or a default value)
                const updatedData = result.data.map(item => ({
                    ...item,
                    hearing_time: item.hearing_time || '11:00' // Default time if not in backend
                }));
                setObjectionData(updatedData || []);
            } else if (Array.isArray(result)) {
                const updatedData = result.map(item => ({
                    ...item,
                    hearing_time: item.hearing_time || '11:00'
                }));
                setObjectionData(updatedData);
            }
        } catch (err) {
            console.error("Error fetching objection data:", err);
            setObjectionData([]);
        }
    };

    const fetchusersLAO = async () => {
        const role = 3;
        setLoadingusersLAO(true);
        try {
            const response = await apiService.getAssignLAO(role, projectId, proposalId);
            if (response.status === 200 && response.data) {
                setusersLAO(Array.isArray(response.data) ? response.data : []);
            } else {
                setusersLAO([]);
            }
        } catch (error) {
            console.error('Error fetching LAO users:', error);
            setusersLAO([]);
        } finally {
            setLoadingusersLAO(false);
        }
    };

    const openEditModal = () => {
        setOriginalFormData(formData);
        setIsEditing(true);
    };

    // Form data state for global fields
    const [formData, setFormData] = useState({
        office_name: '',
        branch_name: '',
        address: '',
        email: '',
        phone: '',
        work_description_no: '',
        date: '',
        application_date: '',
        case_number: '',
        hearing_address: '', // Global default if needed
        time: '',
    });

    const fetchProposalData = async () => {
        if (!projectId || !proposalId) return;
        await fetchusersLAO();
        const result = await apiService.getProposalPublish(projectId, proposalId);
        if (result.status === 200 && result.data) {
            const item = result.data;
            setFormData({
                office_name: item.section_11_office_name || 'जिल्हाधिकारी कार्यालय, बुलढाणा',
                branch_name: item.section_11_branch_name || 'भूसंपादन (इमारत व दळणवळण)',
                address: item.section_11_address || 'स्टेट बँक चौक, बुलढाणा',
                email: item.section_15_email || 'collector.buldhana@gov.in',
                phone: item.section_15_mobile_no || '07265-252525',
                work_description_no: item.section_11_notification || item.section_15_notification || '',
                date: item.section_15_date ? new Date(item.section_15_date).toISOString().split('T')[0] : '',
                application_date: item.section_15_application_date ? new Date(item.section_15_application_date).toISOString().split('T')[0] : '',
                case_number: item.section_11_bhusampadan_no || '',
                hearing_address: item.section_15_notice_representative_address || 'जिल्हाधिकारी कार्यालय, बुलढाणा',
                time: item.section_15_notice_submission_time || '14:56:30',
            });
        }
    };

    // Fetch data when component mounts
    useEffect(() => {
        const fetchNoticeData = async () => {
            if (!projectId || !proposalId || hasFetchedRef.current) {
                return;
            }
            hasFetchedRef.current = true;
            setLoading(true);

            try {
                await Promise.all([
                    fetchProposalData(),
                    fetchObjectionData()
                ]);
            } catch (error) {
                console.error('Error fetching data:', error);
                alert('Error fetching notice data.');
            } finally {
                setLoading(false);
            }
        };

        if (projectId && proposalId) {
            fetchNoticeData();
        }
    }, [projectId, proposalId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!proposalId) {
            alert('Proposal ID not found. Cannot save.');
            return;
        }

        const changedData = {};
        const keyMap = {
            office_name: 'section_11_office_name',
            branch_name: 'section_11_branch_name',
            address: 'section_11_address',
            email: 'section_15_email',
            phone: 'section_15_mobile_no',
            work_description_no: 'section_15_notification',
            date: 'section_15_date',
            application_date: 'section_15_application_date',
            case_number: 'section_11_bhusampadan_no',
            hearing_address: 'section_15_notice_representative_address',
            time: 'section_15_notice_submission_time',
        };

        Object.keys(formData).forEach((key) => {
            if (originalFormData && formData[key] !== originalFormData[key]) {
                if (keyMap[key]) {
                    changedData[keyMap[key]] = formData[key];
                }
            }
        });

        if (Object.keys(changedData).length === 0) {
            alert("No changes were made.");
            setIsEditing(false);
            return;
        }

        setSaving(true);
        try {
            const result = await apiService.updateProposalPublish(proposalId, changedData);
            if (result.status === 200) {
                alert('Details updated successfully!');
                setIsEditing(false);
                hasFetchedRef.current = false;
                await fetchProposalData();
            } else {
                alert(`Failed to update details: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error updating notice data:', error);
            alert('An error occurred while saving. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB').replace(/\//g, '/');
    };

    const formatTime = (timeValue) => {
        if (!timeValue) return '';
        if (timeValue.includes('T')) {
            return timeValue.split('T')[1].substring(0, 8);
        }
        if (timeValue.includes(':')) {
            return timeValue.substring(0, 8);
        }
        return '';
    };

    const handlePrintView = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to use the print feature.');
            return;
        }

        const content = notificationContentRef.current.innerHTML;
        const printHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>कलम 15 ची सूचना</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        @page {
          size: A4 portrait;   
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: black;
          background: white;
        }
        
        .print-container {
          width: 100%;
          max-width: none;
          background: white;
          padding: 20px;
        }
        
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .font-bold { font-weight: bold; }
        .underline { text-decoration: underline; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-6 { margin-bottom: 1.5rem; }
        .mt-8 { margin-top: 2rem; }
        .mx-auto { margin-left: auto; margin-right: auto; }
        .w-16 { width: 5rem; }
        .indent-8 { text-indent: 2rem; }
        
        .header-section {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .contact-file {
            font-size: 12px;
            margin-bottom: 15px;
        }

        .contact-file .top {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
        }

        .contact-file .bottom {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            border-top: 2px solid black;
            padding-top: 3px;
            margin-top: 3px;
        }
        
        .horizontal-line {
          border: none;
          border-top: 2px solid black;
          margin: 0.5rem 0;
        }
        
        p {
          margin: 0.75rem 0;
          text-align: justify;
          line-height: 1.8;
        }
        
        .subject-line {
          font-weight: bold;
          text-align: center;
          margin: 1.5rem 0;
        }
        
        .content-section {
          margin: 2rem 0;
          text-align: justify;
        }
        
        .signature-section {
          text-align: right;
          margin-top: 3rem;
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">${content}</div>
      <script>
        window.onload = function() {
          window.focus();
          window.print();
        };
      </script>
    </body>
    </html>
    `;

        printWindow.document.write(printHTML);
        printWindow.document.close();
    };

    const startEditObjection = (objection) => {
        setObjectionBackup({ ...objection });
        setEditingObjectionId(objection.objection_id);
    };

    const cancelEditObjection = () => {
        const index = objectionData.findIndex(obj => obj.objection_id === editingObjectionId);
        if (index !== -1) {
            const newData = [...objectionData];
            newData[index] = { ...objectionBackup };
            setObjectionData(newData);
        }
        setEditingObjectionId(null);
        setObjectionBackup(null);
    };

    const saveEditObjection = async (objectionId) => {
        const objection = objectionData.find(obj => obj.objection_id === objectionId);
        if (!objection) return;

        const updateData = {
            hearing_date: objection.hearing_date,
            hearing_time: objection.hearing_time,
            hearing_venue: objection.hearing_venue,
        };

        try {
            const result = await apiService.patchUpdateGeneralObjection(objectionId, updateData);
            if (result.status === 200) {
                alert('Objection hearing details updated successfully!');
                setEditingObjectionId(null);
                setObjectionBackup(null);
                // Refetch to sync with backend
                await fetchObjectionData();
            } else {
                alert(`Failed to update: ${result.message || 'Unknown error'}`);
                cancelEditObjection();
            }
        } catch (error) {
            console.error('Error updating objection:', error);
            alert('An error occurred while saving. Please try again.');
            cancelEditObjection();
        }
    };

    const handleObjectionChange = (objectionId, field, value) => {
        const index = objectionData.findIndex(obj => obj.objection_id === objectionId);
        if (index !== -1) {
            const newData = [...objectionData];
            newData[index][field] = value;
            setObjectionData(newData);
        }
    };

    const renderNotificationDocument = () => {
        if (objectionData.length === 0) {
            return (
                <div
                    ref={notificationContentRef}
                    className="bg-white shadow-lg rounded-lg border border-gray-300 p-8 max-w-4xl mx-auto"
                    style={{
                        fontFamily: "'Noto Sans Devanagari', Arial, sans-serif",
                        minHeight: '297mm',
                        fontSize: '14px',
                        lineHeight: '1.6'
                    }}
                >
                    <div className="text-center mt-12 text-gray-500">
                        No objection data available. Please ensure objections have been filed.
                    </div>
                </div>
            );
        }

        return (
            <div ref={notificationContentRef}>
                {objectionData.map((objection, objIdx) => (
                    <div
                        key={objIdx}
                        className="bg-white shadow-lg rounded-lg border border-gray-300 p-8 max-w-4xl mx-auto mb-8"
                        style={{
                            fontFamily: "'Noto Sans Devanagari', Arial, sans-serif",
                            height: '100%',
                            maxHeight: '297mm',
                            overflow: 'hidden',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            pageBreakAfter: objIdx < objectionData.length - 1 ? 'always' : 'auto'
                        }}
                    >
                        {/* Header */}
                        <div className="text-center mb-6">
                            <img
                                src="/Emblem_of_India.svg"
                                alt="महाराष्ट्र शासन"
                                className="mx-auto mb-2 w-16"
                            />
                            <div className="font-bold text-lg">महाराष्ट्र शासन</div>
                            <div className="font-bold text-lg">महसूल व वन विभाग</div>
                            <div className="font-bold text-xl mt-2">{formData.office_name}</div>
                            <div className="font-medium text-base">शाखा :- {formData.branch_name}</div>
                            <div className="text-sm">{formData.address}</div>
                        </div>

                        {/* Contact Information */}
                        <div className="text-xs mb-4 contact-file">
                            <div className="flex justify-between font-semibold top">
                                <div>ई-मेल :- <span className="font-bold">{formData.email}</span></div>
                                <div>दुरध्वनी क्रमांक :- {formData.phone}</div>
                            </div>
                            <div className="flex justify-between mt-1 border-t-2 border-black pt-1 font-semibold bottom">
                                <div>कार्यविवरण क्रमांक :- {formData.work_description_no}</div>
                                <div>दिनांक :- {formatDate(formData.date)}</div>
                            </div>
                        </div>

                        {/* Recipient Address - Using Objection Data */}
                        <div className="mb-6 text-sm">
                            <div className="font-bold">प्रति,</div>
                            <div className="ml-4 mt-2">
                                <div>{objection.objector_name}</div>
                                <div>{objection.objector_address}</div>
                                {objection.village_name && (
                                    <div>मौजे {objection.village_name}, ता.{objection.taluka_name}, जि.{objection.dist_name}</div>
                                )}
                            </div>
                        </div>

                        {/* Subject */}
                        <div className="mb-6">
                            <div className="font-bold text-base">
                                विषय :- <span className="font-bold">कलम -11(1) खाली अधिसूचनेवर दाखल केलेल्या हरकतीच्या अनुषंगाने सुनावणीसाठी उपस्थितीत राहणे बाबत</span>
                            </div>
                        </div>

                        {/* Reference */}
                        <div className="mb-6 text-sm">
                            <span className="font-bold">संदर्भ :- </span>
                            आपला अर्ज दिनांक {formatDate(objection.objection_date || formData.application_date)}
                        </div>

                        {/* Main Content */}
                        <div className="content-section space-y-4 text-justify text-sm">
                            <p className="indent-8">
                                उपरोक्त विषयान्वये भूसंपादन प्रकरण <span className="font-bold">({formData.case_number})</span> मध्ये
                                संमुचित शासनाने भूमिसंपादन, पुनर्वसन व पुनर्वसाहत करताना उचित भरपाई मिळण्याचा व
                                पारदर्शकतेचा हक्क अधिनियम-2013 चे कलम -11(1) खाली अधिसूचना निर्गमित करून
                                आवश्यक त्या ठिकाणी प्रसिध्द केलेली आहे.
                            </p>

                            <p className="indent-8">
                                सदर अधिसूचना प्रसिद्धीनंतर आपली संदर्भीय हरकत या कार्यालयास प्राप्त झाली असून
                                सदर हरकतीच्या अनुषंगाने आपण स्वत: किंवा आपले प्रतिनिधी/अभिकर्तुयामार्फत
                                दिनांक <span className="font-bold">{formatDate(objection.hearing_date || formData.hearing_date)}</span> रोजी
                                सकाळी <span className="font-bold">{formatTime(objection.hearing_time || formData.time)}</span> वाजता
                                <span className="font-bold"> {objection.hearing_venue || formData.hearing_address}</span> येथे आपले मुहणणे
                                मांडण्यास हजर रहावे.
                            </p>

                            <p className="indent-8">
                                सदर तारखेस आपण उपस्थित न राहिल्यास आपले काहीही मुहणणे नाही, असे गृहीत धरून
                                पुढील नियमोचित कार्यवाही करण्यात येईल, याची नोंद घ्यावी.
                            </p>
                        </div>

                        {/* Signature */}
                        <div className="signature-section mt-12 text-right text-sm">
                            <div className="font-bold">{laoDesi}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderEditModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">संपादन करा</h2>
                        <button
                            onClick={() => setIsEditing(false)}
                            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="space-y-8">
                        {/* Notice Details */}
                        <div className="mb-6">
                            <div className="flex items-center justify-center mb-6">
                                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-2 rounded-full">
                                    <h2 className="font-bold text-lg">सूचना तपशील</h2>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="group">
                                    <label className="block font-semibold text-gray-700 mb-2">
                                        कार्यविवरण क्रमांक:
                                    </label>
                                    <input
                                        type="text"
                                        name="work_description_no"
                                        value={formData.work_description_no}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                                    />
                                </div>
                                <div className="group">
                                    <label className="block font-semibold text-gray-700 mb-2">
                                        दिनांक:
                                    </label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Objection Hearing Details */}
                        <div className="mb-6">
                            <div className="flex items-center justify-center mb-6">
                                <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-2 rounded-full">
                                    <h2 className="font-bold text-lg">आक्षेप सुनावणी तपशील</h2>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">आक्षेपकर्त्याचे नाव</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">सुनावणी दिनांक</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">सुनावणी वेळ</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">सुनावणी पत्ता</th>
                                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">क्रिया</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {objectionData.map((objection) => {
                                            const isEditingThis = editingObjectionId === objection.objection_id;
                                            return (
                                                <tr key={objection.objection_id} className="border-t">
                                                    <td className="px-6 py-4 text-sm text-gray-900">{objection.objector_name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        {isEditingThis ? (
                                                            <input
                                                                type="date"
                                                                value={objection.hearing_date ? new Date(objection.hearing_date).toISOString().split('T')[0] : ''}
                                                                onChange={(e) => handleObjectionChange(objection.objection_id, 'hearing_date', e.target.value)}
                                                                className="w-full px-2 py-1 border rounded"
                                                            />
                                                        ) : (
                                                            formatDate(objection.hearing_date)
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        {isEditingThis ? (
                                                            <input
                                                                type="time"
                                                                value={formatTime(objection.hearing_time)}
                                                                onChange={(e) => handleObjectionChange(objection.objection_id, 'hearing_time', e.target.value)}
                                                                className="w-full px-2 py-1 border rounded"
                                                            />
                                                        ) : (
                                                            formatTime(objection.hearing_time)
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900">
                                                        {isEditingThis ? (
                                                            <input
                                                                type="text"
                                                                value={objection.hearing_venue}
                                                                onChange={(e) => handleObjectionChange(objection.objection_id, 'hearing_venue', e.target.value)}
                                                                className="w-full px-2 py-1 border rounded"
                                                            />
                                                        ) : (
                                                            objection.hearing_venue
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        {isEditingThis ? (
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() => saveEditObjection(objection.objection_id)}
                                                                    className="text-green-600 hover:text-green-800"
                                                                >
                                                                    <Save className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={cancelEditObjection}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <RotateCcw className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => startEditObjection(objection)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                            >
                                रद्द करा
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                                {saving && (
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                )}
                                <span>{saving ? 'जतन करीत आहे' : 'जतन करा'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            {/* Action Buttons */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-wrap gap-3 justify-start">
                        {/* {userRole === ROLES.LAO && ( */}
                        {/* Data Entry */}
                        {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                            <button
                                onClick={openEditModal}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                            >
                                <Edit3 className="h-4 w-4 mr-2" />
                                {translations[language]?.edit}
                            </button>
                        )}

                        <button
                            onClick={handlePrintView}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                        >
                            <Printer className="h-4 w-4 mr-2" />
                            <span>{translations[language]?.Print}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Notification Document */}
            {renderNotificationDocument()}

            {/* Edit Modal */}
            {isEditing && renderEditModal()}
        </div>
    );
};

export default Section15Notice;